import React from 'react';
import { ValueEntryModal } from '../../../../../../components/ValueEntryModal/ValueEntryModal';
import type { ValueEntryResult } from '../../../../../../components/ValueEntryModal/ValueEntryModal';
import type { DiscountMode } from '../global-discount-modal/GlobalDiscountModal';
import classes from './item-discount-modal.module.scss';

export type ItemDiscountModalProps = {
  isOpen: boolean;
  itemsSubtotal: number;
  /** ceiling = cheapest selected item's unitPrice */
  maxDiscountAmount: number;
  /** true when at least one selected line already has a discount — shows the override warning */
  hasExistingDiscount?: boolean;
  onClose: () => void;
  onBack: () => void;
  /** discountValue = raw user input (₪ amount or % number); mode tells the caller how to resolve per-line */
  onConfirm: (discountValue: number, mode: DiscountMode) => void;
  isSaving?: boolean;
  initialMode?: DiscountMode;
  onRemoveDiscount?: () => void;
  /** non-managers can't remove a discount directly — show this instead of the remove button */
  removeDisabledMessage?: string;
};

// 2-step item-discount entry (select items → enter discount). Step 2 reuses the shared ValueEntryModal;
// the step indicator is passed as its headerSlot. Step 1 is the upstream ItemDiscountSelectModal.
const StepIndicator: React.FC<{ onBack: () => void }> = ({ onBack }) => (
  <div className={classes.steps}>
    <button type="button" className={`${classes.stepDot} ${classes.stepDotBack}`} onClick={onBack} aria-label="חזור לבחירת פריטים" data-testid="item-discount-step-dot">
      1
    </button>
    <button type="button" className={classes.stepLabelBtn} onClick={onBack} data-testid="item-discount-step-label">
      בחירת פריטים
    </button>
    <span className={classes.stepLine} />
    <span className={`${classes.stepDot} ${classes.stepDotOn}`}>2</span>
    <span className={`${classes.stepLabel} ${classes.stepLabelOn}`}>סוג הנחה</span>
  </div>
);

const ItemDiscountModalComponent: React.FC<ItemDiscountModalProps> = ({
  isOpen,
  itemsSubtotal,
  maxDiscountAmount,
  hasExistingDiscount = false,
  isSaving = false,
  initialMode = 'amount',
  onClose,
  onBack,
  onConfirm,
  onRemoveDiscount,
  removeDisabledMessage,
}) => (
  <ValueEntryModal
    isOpen={isOpen}
    title="הנחה לפריטים"
    navyHeader
    initialMode={initialMode}
    headerSlot={<StepIndicator onBack={onBack} />}
    percentBase={maxDiscountAmount}
    infoRightLabel="פריטים לפני הנחה"
    infoRightValue={itemsSubtotal}
    inputLabels={{ percentage: 'אחוז הנחה', amount: 'סכום הנחה' }}
    maxPercent={100}
    maxAmount={maxDiscountAmount}
    capPercentMessage="אחוז הנחה חייב להיות בין 0 ל-100"
    capAmountMessage="ההנחה גבוהה מסכום הפריט"
    existingAmount={hasExistingDiscount ? 1 : 0}
    showReplaceWarning
    overrideReplaceMessage="קיימת הנחה פעילה, הפעולה תדרוס את ההנחה הקיימת"
    removeLabel="הסר הנחה"
    removeDisabledMessage={removeDisabledMessage}
    dataTestId="item-discount"
    isSaving={isSaving}
    onClose={onClose}
    onConfirm={(result: ValueEntryResult) => onConfirm(result.rawInput, result.mode)}
    onRemove={onRemoveDiscount}
  />
);

export const ItemDiscountModal = React.memo(ItemDiscountModalComponent);
